// calendar-md.ts — the pure markdown/date format layer for calendar-sync,
// extracted verbatim from calendar-sync.ts (Modernization Tier 7 monolith split):
// the YYMMDD<->Date helpers, the RRULE shorthand codec, the `Title > YYMMDD …`
// event-line builder, the event-line finder, the ## Events insertion, and the
// daily-note event parser. Pure string/date transforms over the shared
// calendar-format constants — calls no provider/registry/sync code. calendar-sync.ts
// imports back the eight the two sync directions use; this module imports nothing
// from calendar-sync.ts, so the edge is one-way (no cycle).

import { DAILY_NOTE_SUFFIX, EVENTS_HEADING } from '../utils/calendar-format.js';

// ── Date helpers ─────────────────────────────────────────────────────────────

/**
 * @param {string} yymmdd
 * @returns {Date} a local-time date.
 */
export function yymmddToDate(yymmdd: any) {
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = parseInt(yymmdd.slice(2, 4), 10) - 1;
  const dd = parseInt(yymmdd.slice(4, 6), 10);
  return new Date(2000 + yy, mm, dd);
}

// All-day events are floating wall-clock DATE values (RFC 5545 §3.6.1); the
// codec represents them internally as UTC-midnight ISO (see ical-codec
// _parseDt). yymmddToDate / toYYMMDD work in LOCAL time — correct for timed
// events (the markdown HHMM is the user's wall-clock) but WRONG for all-day,
// where reading getUTC* off a local-midnight instant shifts the date a whole
// day for any user east or west of UTC. These UTC-anchored variants keep the
// all-day round-trip (markdown ⇄ provider) stable in every timezone and align
// the markdown representation with the codec's parse contract.
/**
 * @param {string} yymmdd
 * @returns {Date} a UTC-anchored date.
 */
function yymmddToUtcDate(yymmdd: any) {
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = parseInt(yymmdd.slice(2, 4), 10) - 1;
  const dd = parseInt(yymmdd.slice(4, 6), 10);
  return new Date(Date.UTC(2000 + yy, mm, dd));
}

/**
 * @param {Date} d
 * @returns {string} YYMMDD in UTC.
 */
export function utcDateToYYMMDD(d: any) {
  const yy = String(d.getUTCFullYear()).slice(-2);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return yy + mm + dd;
}

/**
 * @param {string} hhmm
 * @returns {{ h: number, m: number }}
 */
export function hhmmToTime(hhmm: any) {
  return { h: parseInt(hhmm.slice(0, 2), 10), m: parseInt(hhmm.slice(2), 10) };
}

/**
 * @param {string} yymmdd
 * @returns {string} the daily-note name.
 */
export function dailyNoteName(yymmdd: any) {
  return yymmdd + DAILY_NOTE_SUFFIX;
}

// ── RRULE shorthand ──────────────────────────────────────────────────────────
//
// Markdown lines carry recurrence as `~weekly` / `~rrule:FREQ=…`. The parse
// and format helpers live in providers/ical-codec.js so the same vocabulary
// works on the network side too. Markdown tokens are whitespace-free, so we
// hop the space-form ("every 2 weeks") through hyphens ("every-2-weeks").
// ical-codec is a sibling provider leaf (it does NOT import calendar-sync, so
// this is a one-way edge, no cycle). It exports parseRruleShorthand /
// formatRruleShorthand / expandRrule as named exports, so the namespace import
// has them directly. (Was a jest-CJS `require()` feature-detect with a
// `window.ICalCodec` fallback — a migration-era kludge that neither Vitest nor
// the browser ESM build could satisfy; a real import is correct everywhere.)
import * as _IcalCodec from './providers/ical-codec.js';

/**
 * @param {string | null} [rrule]
 * @returns {string | null} the markdown shorthand token, or null.
 */
function _rruleToToken(rrule: any) {
  if (!rrule) return null;
  if (_IcalCodec?.formatRruleShorthand) {
    const sh = _IcalCodec.formatRruleShorthand(rrule);
    if (sh) return sh.replace(/\s+/g, '-');
  }
  return `rrule:${rrule}`;
}

/**
 * @param {string | null} [token]
 * @returns {string | null} the RFC 5545 RRULE, or null.
 */
function _tokenToRrule(token: any) {
  if (!token) return null;
  if (token.startsWith('rrule:')) return token.slice(6);
  const normalised = token.replace(/-/g, ' ');
  if (_IcalCodec?.parseRruleShorthand) {
    return _IcalCodec.parseRruleShorthand(normalised);
  }
  return null;
}

// ── Markdown event line builder ──────────────────────────────────────────────

/**
 * @param {any} evt - a provider event.
 * @param {Date} evtStart
 * @param {Date} evtEnd
 * @param {string} dateStr
 * @param {string | null} [calendarTag]
 * @returns {string} the markdown event line.
 */
export function buildMdLine(evt: any, evtStart: any, evtEnd: any, dateStr: any, calendarTag: any) {
  const tagSuffix = calendarTag ? ` @${calendarTag}` : '';
  const rruleToken = _rruleToToken(evt?.rrule);
  const ruleSuffix = rruleToken ? ` ~${rruleToken}` : '';
  // Providers very occasionally return events with no title (Google calls
  // them "(No title)" but Microsoft returns null subject for some
  // calendar-blocking entries). Render an explicit placeholder rather
  // than letting "null" or "undefined" land as literal text in the user's
  // daily note.
  const title = evt && evt.title != null && evt.title !== '' ? String(evt.title) : '(untitled)';
  if (evt.allDay) {
    // All-day dates are UTC-anchored floating dates — format with getUTC*
    // so the rendered YYMMDD doesn't drift a day for users away from UTC.
    const startStr = utcDateToYYMMDD(evtStart);
    const endDateAdj = new Date(evtEnd);
    endDateAdj.setUTCDate(endDateAdj.getUTCDate() - 1);
    const endStr = utcDateToYYMMDD(endDateAdj);
    if (startStr !== endStr && endStr > startStr) {
      return `${title} > ${startStr} ${endStr}${tagSuffix}${ruleSuffix}`;
    } else {
      return `${title} > ${startStr}${tagSuffix}${ruleSuffix}`;
    }
  } else {
    const hh1 = String(evtStart.getHours()).padStart(2, '0');
    const mm1 = String(evtStart.getMinutes()).padStart(2, '0');
    const hh2 = String(evtEnd.getHours()).padStart(2, '0');
    const mm2 = String(evtEnd.getMinutes()).padStart(2, '0');
    return `${title} > ${dateStr} ${hh1}${mm1} ${hh2}${mm2}${tagSuffix}${ruleSuffix}`;
  }
}

// ── Find event line in note ──────────────────────────────────────────────────
// Returns the line index of a synced event, or -1 when the line can't be
// located unambiguously.
//
// The previous implementation fell back to the stored lineIndex whenever
// the lineText lookup missed. If the user had edited the note in the
// meantime (added/removed lines), that index now pointed at an unrelated
// line — a task, heading, or unrelated paragraph — and the caller would
// happily overwrite it with the updated calendar syntax. This guards
// against that:
//   • lineText present + matches → use it (canonical case).
//   • lineText present + no match → return -1. The user edited or moved
//     the line; the next sync will re-insert it under ## Events if the
//     event is still on the server. Corrupting an unrelated line is
//     worse than briefly losing the markdown row.
//   • lineText absent (legacy metadata pre-lineText) → use the stored
//     lineIndex only if the line at it still LOOKS like an event line
//     (contains "> " followed by a six-digit date token).

/**
 * @param {string[]} lines
 * @param {any} metaEvent - a stored metadata event entry.
 * @param {Set<number>} [claimed] - line indices already bound to another entry
 *   in this pass; skipped so two byte-identical event lines (same title + time
 *   on the same day, distinct providerIds) resolve to DISTINCT lines instead of
 *   both grabbing the first match.
 * @returns {number} the line index, or -1.
 */
export function findEventLine(lines: any, metaEvent: any, claimed?: Set<number>) {
  if (metaEvent.lineText) {
    const target = metaEvent.lineText.trim();
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].trim() === target && !claimed?.has(i)) return i;
    }
    return -1;
  }
  if (metaEvent.lineIndex != null && metaEvent.lineIndex < lines.length) {
    const candidate = lines[metaEvent.lineIndex] || '';
    if (/>\s+\d{6}\b/.test(candidate) && !claimed?.has(metaEvent.lineIndex)) {
      return metaEvent.lineIndex;
    }
  }
  return -1;
}

// ── Insert events under ## Events heading ────────────────────────────────────
// Inserts newLines under the "## Events" subheading that sits just after the
// "# Title" line. Creates the heading (and a surrounding blank line) if absent.
// Returns { content, startLineIdx } where startLineIdx is the index of the
// first newly inserted line.

/**
 * @param {string} content
 * @param {string[]} newLines
 * @returns {{ content: string, startLineIdx: number }}
 */
export function insertEventsUnderHeading(content: any, newLines: any) {
  // EVENTS_HEADING imported from utils/calendar-format.js (shared with merge guards).
  const lines = content.split('\n');

  let titleIdx = 0;
  for (let i = 0; i < lines.length; i++) {
    if (/^#\s/.test(lines[i])) {
      titleIdx = i;
      break;
    }
  }

  let eventsIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim() === EVENTS_HEADING) {
      eventsIdx = i;
      break;
    }
  }

  let insertIdx: any;
  if (eventsIdx !== -1) {
    let pos = eventsIdx + 1;
    while (pos < lines.length && !/^##?\s/.test(lines[pos])) pos++;
    while (pos > eventsIdx + 1 && lines[pos - 1].trim() === '') pos--;
    insertIdx = pos;
  } else {
    const sectionLines = ['', EVENTS_HEADING, ''];
    lines.splice(titleIdx + 1, 0, ...sectionLines);
    insertIdx = titleIdx + 1 + sectionLines.length;
  }

  lines.splice(insertIdx, 0, ...newLines);
  return { content: lines.join('\n'), startLineIdx: insertIdx };
}

// ── Parse markdown events from daily note ────────────────────────────────────

// Pull off optional `@tag` and `~rrule_token` suffixes in any order. The
// caller is left with the bare `Text > date…` body to feed into the date
// regex. Up to one of each suffix is recognised.
/**
 * @param {string} line
 * @returns {{ body: string, calendarTag: string | null, rrule: string | null }}
 */
function _extractSuffixes(line: any) {
  let tag = null,
    rruleToken = null;
  let cur = line;
  for (let i = 0; i < 2; i++) {
    const r = cur.match(/\s+~(\S+)\s*$/);
    if (r && rruleToken == null) {
      rruleToken = r[1];
      cur = cur.slice(0, r.index);
      continue;
    }
    const t = cur.match(/\s+@(\S+)\s*$/);
    if (t && tag == null) {
      tag = t[1];
      cur = cur.slice(0, t.index);
      continue;
    }
    break;
  }
  return { body: cur, calendarTag: tag, rrule: _tokenToRrule(rruleToken) };
}

/**
 * @param {string} content
 * @returns {any[]} parsed markdown events.
 */
export function parseMarkdownEvents(content: any) {
  const events = [];
  const lines = content.split('\n');
  const reT = /^(.+?)\s*>\s*(\d{6})\s+(\d{4})\s+(\d{4})\s*$/; // timed
  const reM = /^(.+?)\s*>\s*(\d{6})\s+(\d{6})\s*$/; // multi-day
  const reA = /^(.+?)\s*>\s*(\d{6})\s*$/; // all-day

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i].trim();
    if (/^- \[[ xX]\]/.test(raw)) continue; // skip tasks
    const { body, calendarTag, rrule } = _extractSuffixes(raw);

    let m: any;
    if ((m = body.match(reT))) {
      const text = m[1]
        .replace(/^[-*+]\s+/, '')
        .replace(/^#+\s*/, '')
        .trim();
      if (!text) continue;
      const date = yymmddToDate(m[2]);
      const start = hhmmToTime(m[3]);
      const end = hhmmToTime(m[4]);
      const startDate = new Date(date);
      startDate.setHours(start.h, start.m, 0, 0);
      const endDate = new Date(date);
      endDate.setHours(end.h, end.m, 0, 0);
      events.push({ text, startDate, endDate, allDay: false, lineIndex: i, calendarTag, rrule });
    } else if ((m = body.match(reM))) {
      const text = m[1]
        .replace(/^[-*+]\s+/, '')
        .replace(/^#+\s*/, '')
        .trim();
      if (!text) continue;
      const endDate = yymmddToUtcDate(m[3]);
      endDate.setUTCDate(endDate.getUTCDate() + 1); // inclusive markdown end → exclusive provider end
      events.push({
        text,
        startDate: yymmddToUtcDate(m[2]),
        endDate,
        allDay: true,
        lineIndex: i,
        calendarTag,
        rrule,
      });
    } else if ((m = body.match(reA))) {
      const text = m[1]
        .replace(/^[-*+]\s+/, '')
        .replace(/^#+\s*/, '')
        .trim();
      if (!text) continue;
      const date = yymmddToUtcDate(m[2]);
      const endDate = new Date(date);
      endDate.setUTCDate(endDate.getUTCDate() + 1);
      events.push({
        text,
        startDate: date,
        endDate,
        allDay: true,
        lineIndex: i,
        calendarTag,
        rrule,
      });
    }
  }
  return events;
}
